import React, { useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faXmark } from '@fortawesome/free-solid-svg-icons'
import type { PermissionLevel } from '../../services/permissionService'
import {
  NOTIFICATION_TYPES,
  type NotificationMatrixToggle,
  type NotificationTypeDef,
  type NotificationTypeKey,
} from '../../config/notificationTypes'

type ChannelFlags = {
  push: boolean
  alert: boolean
  email: boolean
  user: boolean
  fan: boolean
}

export type ShiftReminderForm = {
  clockOutAfterHours: number
  dailyFormsAfterHours: number
  dailyFormsFolders: string[]
  reminderResendAfterHours: number
  reminderMaxSends: number
  folderDraft: string
}

type NotificationSettingsPanelProps = {
  permissionLevels: PermissionLevel[]
  permissionsLoading: boolean
  permissionsError: string | null
  onDismissPermissionsError: () => void
  getChannelFlags: (
    level: PermissionLevel,
    key: NotificationTypeKey,
    eventKey?: string | null,
  ) => ChannelFlags
  onToggle: (
    levelId: string,
    notificationKey: NotificationTypeKey,
    channel: NotificationMatrixToggle,
    eventKey?: string | null,
  ) => void
  shiftSettingsForm: ShiftReminderForm
  onShiftSettingsForm: (updater: (current: ShiftReminderForm) => ShiftReminderForm) => void
  shiftSettingsLoading: boolean
  shiftSettingsSaving: boolean
  shiftSettingsError: string | null
  shiftSettingsSavedAt: string | null
  onDismissShiftError: () => void
  onSaveShiftSettings: () => void
}

const GROUPS: { label: string; keys: NotificationTypeKey[] }[] = [
  { label: 'Shift reminders', keys: ['forgotToClockOut'] },
  { label: 'Records', keys: ['inventory', 'assets', 'certifications', 'municipalityLicenses'] },
]

const DESCRIPTIONS: Record<NotificationTypeKey, string> = {
  forgotToClockOut: 'Sent while someone is still clocked in after the hours below.',
  pto: 'Not sent yet.',
  controlCenterReportDone: 'Not sent yet.',
  inventory: 'Sent when inventory changes. Choose an action, then who should hear about it.',
  assets: 'Sent when an asset changes. Choose an action, then who should hear about it.',
  certifications: 'Sent when a certification is created, updated, or coming due.',
  municipalityLicenses: 'Sent when a municipal license changes or is coming due.',
}

function deliveryOn(flags: ChannelFlags) {
  return flags.push || flags.alert || flags.email
}

function typeHasDelivery(
  def: NotificationTypeDef,
  levels: PermissionLevel[],
  getChannelFlags: NotificationSettingsPanelProps['getChannelFlags'],
) {
  return levels.some((level) => {
    if (def.events?.length) {
      return def.events.some((event) => deliveryOn(getChannelFlags(level, def.key, event.key)))
    }
    return deliveryOn(getChannelFlags(level, def.key))
  })
}

const NotificationSettingsPanel: React.FC<NotificationSettingsPanelProps> = ({
  permissionLevels,
  permissionsLoading,
  permissionsError,
  onDismissPermissionsError,
  getChannelFlags,
  onToggle,
  shiftSettingsForm,
  onShiftSettingsForm,
  shiftSettingsLoading,
  shiftSettingsSaving,
  shiftSettingsError,
  shiftSettingsSavedAt,
  onDismissShiftError,
  onSaveShiftSettings,
}) => {
  const byKey = useMemo(
    () => new Map(NOTIFICATION_TYPES.map((type) => [type.key, type])),
    [],
  )
  const unwired = NOTIFICATION_TYPES.filter((type) => type.wired === false)
  const [selectedKey, setSelectedKey] = useState<NotificationTypeKey>('forgotToClockOut')
  const [eventKey, setEventKey] = useState<string | null>(null)
  const [showUnwired, setShowUnwired] = useState(false)

  const selected = byKey.get(selectedKey) ?? NOTIFICATION_TYPES[0]
  const events = selected.events ?? []
  const activeEvent = events.find((event) => event.key === eventKey)?.key ?? events[0]?.key ?? null
  const wired = selected.wired !== false
  const fanOnly = !!selected.fanOnly

  const selectType = (key: NotificationTypeKey) => {
    setSelectedKey(key)
    const next = byKey.get(key)
    setEventKey(next?.events?.[0]?.key ?? null)
    if (next?.wired === false) setShowUnwired(true)
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold">Notifications</h2>
        <p className="text-sm text-base-content/70">
          Pick a notification, then choose who receives it and how. Changes to Email, Push, and Inbox save immediately.
        </p>
      </div>

      {permissionsError && (
        <div className="alert alert-error">
          <span>{permissionsError}</span>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onDismissPermissionsError} aria-label="Dismiss">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-4 items-start">
        <nav className="w-full lg:w-72 shrink-0 rounded-lg border border-base-300 p-2" aria-label="Notification types">
          {GROUPS.map((group) => (
            <div key={group.label} className="mb-2">
              <div className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-base-content/50">
                {group.label}
              </div>
              {group.keys.map((key) => {
                const type = byKey.get(key)
                if (!type) return null
                const on = typeHasDelivery(type, permissionLevels, getChannelFlags)
                const active = selected.key === key
                return (
                  <button
                    key={key}
                    type="button"
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between gap-2 ${
                      active ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-base-200'
                    }`}
                    aria-current={active ? 'true' : undefined}
                    onClick={() => selectType(key)}
                  >
                    <span>{type.label}</span>
                    {on ? <span className="text-[10px] uppercase tracking-wide">On</span> : null}
                  </button>
                )
              })}
            </div>
          ))}
          {unwired.length > 0 && (
            <div>
              <button
                type="button"
                className="w-full text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-base-content/50"
                aria-expanded={showUnwired}
                onClick={() => setShowUnwired((open) => !open)}
              >
                Not available yet
              </button>
              {showUnwired && unwired.map((type) => {
                const active = selected.key === type.key
                return (
                  <button
                    key={type.key}
                    type="button"
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm text-base-content/60 ${
                      active ? 'bg-base-200 font-medium' : 'hover:bg-base-200'
                    }`}
                    aria-current={active ? 'true' : undefined}
                    onClick={() => selectType(type.key)}
                  >
                    {type.label}
                  </button>
                )
              })}
            </div>
          )}
        </nav>

        <section className="flex-1 min-w-0 space-y-4 w-full">
          <div>
            <h3 className="text-lg font-semibold">{selected.label}</h3>
            <p className="text-sm text-base-content/70">{DESCRIPTIONS[selected.key]}</p>
            {!wired && (
              <p className="text-sm text-warning mt-1">This notification is not sent yet.</p>
            )}
          </div>

          {selected.key === 'forgotToClockOut' && (
            <ShiftSettingsCard
              form={shiftSettingsForm}
              onForm={onShiftSettingsForm}
              loading={shiftSettingsLoading}
              saving={shiftSettingsSaving}
              error={shiftSettingsError}
              savedAt={shiftSettingsSavedAt}
              onDismissError={onDismissShiftError}
              onSave={onSaveShiftSettings}
            />
          )}

          {events.length > 0 && (
            <div className="flex flex-wrap gap-2" role="tablist" aria-label={`${selected.label} actions`}>
              {events.map((event) => (
                <button
                  key={event.key}
                  type="button"
                  role="tab"
                  aria-selected={activeEvent === event.key}
                  className={`btn btn-sm ${activeEvent === event.key ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setEventKey(event.key)}
                >
                  {event.label}
                </button>
              ))}
            </div>
          )}

          {permissionsLoading ? (
            <div className="flex justify-center py-12">
              <FontAwesomeIcon icon={faSpinner} className="text-2xl animate-spin" />
            </div>
          ) : permissionLevels.length === 0 ? (
            <div className="rounded-lg border border-base-300 p-8 text-center text-base-content/70">
              No access levels found. Add levels under Permissions first.
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-base-content/60">
                {fanOnly
                  ? 'These go to everyone on the access level. Push also turns on Inbox. Turning Inbox off turns Push off.'
                  : 'This person is who the event is about. Everyone on this level is the rest of that access level. At least one of those stays on while a delivery method is on. Push also turns on Inbox.'}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {permissionLevels.map((level) => (
                  <LevelCard
                    key={level._id}
                    level={level}
                    notificationKey={selected.key}
                    eventKey={events.length ? activeEvent : null}
                    rowLabel={events.length
                      ? `${selected.label} — ${events.find((event) => event.key === activeEvent)?.label ?? ''}`
                      : selected.label}
                    wired={wired}
                    fanOnly={fanOnly}
                    flags={getChannelFlags(level, selected.key, events.length ? activeEvent : null)}
                    onToggle={onToggle}
                  />
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

function LevelCard({
  level,
  notificationKey,
  eventKey,
  rowLabel,
  wired,
  fanOnly,
  flags,
  onToggle,
}: {
  level: PermissionLevel
  notificationKey: NotificationTypeKey
  eventKey: string | null
  rowLabel: string
  wired: boolean
  fanOnly: boolean
  flags: ChannelFlags
  onToggle: NotificationSettingsPanelProps['onToggle']
}) {
  const sending = deliveryOn(flags)
  const toggle = (channel: NotificationMatrixToggle) => {
    if (!wired) return
    onToggle(level._id, notificationKey, channel, eventKey)
  }

  return (
    <div className={`rounded-lg border border-base-300 p-4 space-y-3 ${wired ? '' : 'opacity-60'}`}>
      <h4 className="font-semibold">{level.name}</h4>
      <fieldset className="space-y-2" disabled={!wired}>
        <legend className="text-xs font-semibold uppercase tracking-wide text-base-content/50">Who</legend>
        {!fanOnly && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              checked={!!flags.user && sending}
              aria-label={`This person for ${rowLabel} — ${level.name}`}
              onChange={() => toggle('user')}
            />
            This person
          </label>
        )}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="checkbox checkbox-sm"
            checked={!!flags.fan && sending}
            aria-label={`Everyone on this level for ${rowLabel} — ${level.name}`}
            onChange={() => toggle('fan')}
          />
          Everyone on this level
        </label>
      </fieldset>
      <fieldset className="space-y-2" disabled={!wired}>
        <legend className="text-xs font-semibold uppercase tracking-wide text-base-content/50">How</legend>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="checkbox checkbox-sm"
            checked={!!flags.email}
            aria-label={`Email for ${rowLabel} — ${level.name}`}
            onChange={() => toggle('email')}
          />
          Email
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="checkbox checkbox-sm"
            checked={!!flags.push}
            aria-label={`Push for ${rowLabel} — ${level.name}`}
            onChange={() => toggle('push')}
          />
          Push
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="checkbox checkbox-sm"
            checked={!!flags.alert}
            aria-label={`Inbox for ${rowLabel} — ${level.name}`}
            onChange={() => toggle('alert')}
          />
          Inbox
        </label>
      </fieldset>
    </div>
  )
}

function ShiftSettingsCard({
  form,
  onForm,
  loading,
  saving,
  error,
  savedAt,
  onDismissError,
  onSave,
}: {
  form: ShiftReminderForm
  onForm: (updater: (current: ShiftReminderForm) => ShiftReminderForm) => void
  loading: boolean
  saving: boolean
  error: string | null
  savedAt: string | null
  onDismissError: () => void
  onSave: () => void
}) {
  return (
    <div className="rounded-lg border border-base-300 p-4 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h4 className="font-semibold">When to send</h4>
        <button
          type="button"
          className="btn btn-primary btn-sm self-start"
          disabled={loading || saving}
          onClick={onSave}
        >
          {saving ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
              Saving...
            </>
          ) : (
            'Save'
          )}
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onDismissError} aria-label="Dismiss">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-6">
          <FontAwesomeIcon icon={faSpinner} className="text-xl animate-spin" />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="form-control w-full">
            <span className="label-text">Send after (hours clocked in)</span>
            <input
              type="number"
              min={1}
              max={168}
              step={0.5}
              className="input input-bordered w-full"
              value={form.clockOutAfterHours}
              onChange={(e) =>
                onForm((current) => ({
                  ...current,
                  clockOutAfterHours: Number(e.target.value) || 1,
                }))
              }
            />
            <span className="label-text-alt text-base-content/60">Default 10</span>
          </label>
          <label className="form-control w-full">
            <span className="label-text">Wait before sending again (hours)</span>
            <input
              type="number"
              min={0.25}
              max={168}
              step={0.25}
              className="input input-bordered w-full"
              value={form.reminderResendAfterHours}
              onChange={(e) =>
                onForm((current) => ({
                  ...current,
                  reminderResendAfterHours: Number(e.target.value) || 0.25,
                }))
              }
            />
            <span className="label-text-alt text-base-content/60">
              Default 2. Applies to this clock-out reminder, per clock session.
            </span>
          </label>
          <label className="form-control w-full">
            <span className="label-text">Max sends per clock session</span>
            <input
              type="number"
              min={1}
              max={50}
              step={1}
              className="input input-bordered w-full"
              value={form.reminderMaxSends}
              onChange={(e) =>
                onForm((current) => ({
                  ...current,
                  reminderMaxSends: Math.max(1, Math.round(Number(e.target.value) || 1)),
                }))
              }
            />
            <span className="label-text-alt text-base-content/60">
              Default 3. Use 1 for a single reminder.
            </span>
          </label>
          {savedAt && (
            <span className="text-xs text-base-content/50 sm:col-span-2">
              Last saved {new Date(savedAt).toLocaleString()}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export default NotificationSettingsPanel
